/**
 * AUXcanvas TrailerCanvas — visual drag-and-drop trailer slot planner
 *
 * Uses Pointer Events API + setPointerCapture for reliable mouse AND touch drag.
 * No HTML5 drag events. No canvas element (avoids 0x0 canvas bug).
 * Ghost card follows pointer during drag. Slots highlight on hover.
 */

export interface VehicleInfo {
  vehicleId: string
  vin: string
  make?: string
  model?: string
  year?: number
  color?: string
  bodyStyle?: string
  // Vehicle fit profile data (populated after resolve-fit-profiles)
  fitProfileStatus?: 'ready' | 'fallback' | 'missing' | 'needs_review'
  fitProfileWarnings?: string[]
  // Best-available dimensions (from profile or vehicle row)
  lengthIn?: number | null
  heightIn?: number | null
  weightLb?: number | null
  wheelbaseIn?: number | null
  groundClearanceIn?: number | null
  // Risk flags
  isTallVehicle?: boolean
  isLowClearance?: boolean
  isLongWheelbase?: boolean
  isHeavyVehicle?: boolean
  isOversize?: boolean
}

export type SlotId = string

export const SLOT_META: Record<string, { label: string; deck: 'upper' | 'lower'; col: number }> = {
  r1c1: { label: 'Front',   deck: 'upper', col: 1 },
  r1c2: { label: 'Mid-F',   deck: 'upper', col: 2 },
  r1c3: { label: 'Mid-R',   deck: 'upper', col: 3 },
  r1c4: { label: 'Rear',    deck: 'upper', col: 4 },
  r2c1: { label: 'Front',   deck: 'lower', col: 1 },
  r2c2: { label: 'Mid-F',   deck: 'lower', col: 2 },
  r2c3: { label: 'Mid-R',   deck: 'lower', col: 3 },
  r2c4: { label: 'Rear',    deck: 'lower', col: 4 },
}

export const UPPER_SLOTS: SlotId[] = ['r1c1', 'r1c2', 'r1c3', 'r1c4']
export const LOWER_SLOTS: SlotId[] = ['r2c1', 'r2c2', 'r2c3', 'r2c4']
export const ALL_SLOTS: SlotId[] = [...UPPER_SLOTS, ...LOWER_SLOTS]

export function vehicleLabel(v: VehicleInfo): string {
  const parts = [v.year?.toString(), v.make, v.model].filter(Boolean)
  if (parts.length > 0) return parts.join(' ')
  if (typeof v.vin === 'string' && v.vin.length > 0) return `VIN …${v.vin.slice(-6)}`
  return 'Unknown Vehicle'
}

export function vehicleColorHex(color?: string): string {
  if (!color) return '#6B7280'
  if (color.startsWith('#')) return color
  const map: Record<string, string> = {
    white: '#F9FAFB', black: '#1F2937', silver: '#9CA3AF', gray: '#6B7280', grey: '#6B7280',
    red: '#EF4444', blue: '#3B82F6', green: '#22C55E', yellow: '#F59E0B',
    orange: '#F97316', brown: '#92400E', gold: '#D97706', purple: '#8B5CF6',
  }
  return map[color.toLowerCase()] ?? '#6B7280'
}

export interface DragState {
  vehicleId: string
  sourceSlotId: SlotId | null   // null = dragging from queue
  x: number
  y: number
}
